// src/components/Admin/Category/PricingRuleForm.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Textarea } from '../../../ui/textarea';
import { Checkbox } from '../../../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select'; // Updated [cite: 23]
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../../../ui/dialog'; // Updated [cite: 25]
import { XIcon } from 'lucide-react'; // For close button [cite: 9]
import { getPricingConditionDefinitions } from '../../../../services/pricingApi';
//import { cn } from '../../../../lib/utils';

const ModifierTypeEnum = {
    ABSOLUTE: "ABSOLUTE",
    PERCENTAGE: "PERCENTAGE"
};

const ModifierActionEnum = {
    INCREASE: "INCREASE",
    DECREASE: "DECREASE"
};

const defaultRuleValues = {
    rule_name: '',
    description: '',
    is_active: true,
    modifier_type: '', // Default to empty to force selection
    modifier_action: ModifierActionEnum.INCREASE,
    modifier_value_absolute: 0,
    modifier_value_percentage_display: 0,
    condition_definition_id: '',
    condition_config: {}
};

const errorTextClass = "text-sm text-destructive mt-1"; // [cite: 7]

function useDebouncedValue(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => { setDebouncedValue(value); }, delay);
        return () => { clearTimeout(handler); };
    }, [value, delay]);
    return debouncedValue;
}

const DynamicConditionConfigFormComponent = ({
    definition, control, errors, questionList, setValue
}) => {
    const currentSelectedQuestionId = useWatch({ control, name: 'condition_config.category_question_id' });

    const relevantQuestions = useMemo(() => {
        if (!definition || !questionList) return [];
        if (!definition.applicable_question_types_filter || !Array.isArray(definition.applicable_question_types_filter) || definition.applicable_question_types_filter.length === 0) {
            return questionList;
        }
        const cleanFilterTypes = definition.applicable_question_types_filter.map(ft => String(ft || '').trim());
        return questionList.filter(q => {
            const questionTypeClean = String(q.question_type || '').trim();
            return cleanFilterTypes.includes(questionTypeClean);
        });
    }, [questionList, definition]);

    const optionsForSelectedQuestion = useMemo(() => {
        if (!currentSelectedQuestionId || !questionList) return [];
        const foundQuestion = questionList.find(q => q.id === parseInt(currentSelectedQuestionId));
        return foundQuestion ? (foundQuestion.options || []) : [];
    }, [currentSelectedQuestionId, questionList]);

    if (!definition || !definition.condition_code) {
        return <p className="text-xs text-muted-foreground mt-2">Select a condition type above to configure its parameters.</p>;
    }
    const code = definition.condition_code;

    return (
        <div className="space-y-4 p-4 border border-dashed rounded-md mt-3 bg-muted/30"> {/* [cite: 1] */}
            {definition.config_schema_guidance?.notes_for_admin && <p className="text-xs text-muted-foreground italic mb-3">{definition.config_schema_guidance.notes_for_admin}</p>}
            
            { (code === 'Q_MULTI_OPTION_ALL_MATCH' || code === 'Q_SINGLE_OPTION_MATCH') && (
                <>
                    <div>
                        <Label htmlFor="cq_id">Question <span className="text-destructive">*</span></Label>
                        <Controller
                            name="condition_config.category_question_id"
                            control={control}
                            rules={{ required: 'Question is required' }}
                            render={({ field }) => (
                                <Select
                                    value={field.value ? String(field.value) : "None"}
                                    onValueChange={(value) => {
                                        field.onChange(value ? parseInt(value, 10) : '');
                                        if (code === 'Q_MULTI_OPTION_ALL_MATCH') setValue('condition_config.required_option_ids', [], { shouldDirty: true });
                                        if (code === 'Q_SINGLE_OPTION_MATCH') setValue('condition_config.required_option_id', '', { shouldDirty: true });
                                    }}
                                >
                                    <SelectTrigger id="cq_id" className="mt-1">
                                        <SelectValue placeholder="Select a question" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="None">Select a question</SelectItem>
                                        {relevantQuestions.map(q => (
                                            <SelectItem key={q.id} value={String(q.id)}>{q.label || q.value}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        />
                        {errors.condition_config?.category_question_id && <p className={errorTextClass}>{errors.condition_config.category_question_id.message}</p>}
                    </div>
                    {currentSelectedQuestionId && (
                        code === 'Q_MULTI_OPTION_ALL_MATCH' ? (
                            <div>
                                <Label>Required Options (ALL must be selected) <span className="text-destructive">*</span></Label>
                                <Controller
                                    name="condition_config.required_option_ids"
                                    control={control}
                                    rules={{ required: 'At least one option is required', validate: v => (Array.isArray(v) && v.length > 0) || 'Select at least one option' }}
                                    render={({ field }) => (
                                        <div className="space-y-1 max-h-32 overflow-y-auto border p-2 rounded-md bg-background mt-1"> {/* [cite: 1] */}
                                            {optionsForSelectedQuestion.length === 0 && <p className="text-xs text-muted-foreground">No options found.</p>}
                                            {optionsForSelectedQuestion.map(opt => (
                                                <div key={opt.id} className="flex items-center space-x-2">
                                                    <Checkbox
                                                        id={`opt-${opt.id}`}
                                                        checked={field.value?.includes(opt.id)}
                                                        onCheckedChange={checked => {
                                                            const currentValues = field.value || [];
                                                            if (checked) {
                                                                field.onChange([...currentValues, opt.id]);
                                                            } else {
                                                                field.onChange(currentValues.filter(id => id !== opt.id));
                                                            }
                                                        }}
                                                    />
                                                    <Label htmlFor={`opt-${opt.id}`} className="font-normal text-sm">{opt.label || opt.value}</Label>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                />
                                {errors.condition_config?.required_option_ids && <p className={errorTextClass}>{errors.condition_config.required_option_ids.message}</p>}
                            </div>
                        ) : ( // Q_SINGLE_OPTION_MATCH
                            <div>
                                <Label htmlFor="req_opt_id">Required Option <span className="text-destructive">*</span></Label>
                                <Controller
                                    name="condition_config.required_option_id"
                                    control={control}
                                    rules={{ required: 'Option is required' }}
                                    render={({ field }) => (
                                         <Select
                                            value={field.value ? String(field.value) : "None"}
                                            onValueChange={(value) => field.onChange(value ? parseInt(value, 10) : '')}
                                        >
                                            <SelectTrigger id="req_opt_id" className="mt-1">
                                                <SelectValue placeholder="Select an option" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="None">Select an option</SelectItem>
                                                {optionsForSelectedQuestion.length === 0 && <SelectItem value="None" disabled>No options for selected question</SelectItem>}
                                                {optionsForSelectedQuestion.map(opt => (
                                                    <SelectItem key={opt.id} value={String(opt.id)}>{opt.label || opt.value}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                                {errors.condition_config?.required_option_id && <p className={errorTextClass}>{errors.condition_config.required_option_id.message}</p>}
                            </div>
                        )
                    )}
                </>
            )}
            { (code === 'LOC_STATE_MATCH' || code === 'LOC_DISTRICT_MATCH' || code === 'LOC_POSTCODE_MATCH') && (
                <div>
                    <Label htmlFor="val_match">Value to Match <span className="text-destructive">*</span></Label>
                    <Controller
                        name="condition_config.value_to_match"
                        control={control}
                        rules={{ required: "Value is required" }}
                        render={({ field }) => <Input id="val_match" type="text" {...field} value={field.value || ""} className="mt-1" />}
                    />
                    {errors.condition_config?.value_to_match && <p className={errorTextClass}>{errors.condition_config.value_to_match.message}</p>}
                </div>
            )}
            { code === 'EMAIL_DOMAIN_TYPE_MATCH' && (
                <div>
                    <Label htmlFor="exp_dom_type">Expected Domain Type <span className="text-destructive">*</span></Label>
                    <Controller
                        name="condition_config.expected_domain_type"
                        control={control}
                        rules={{ required: "Domain type is required" }}
                        render={({ field }) => (
                            <Select value={field.value || ""} onValueChange={field.onChange}>
                                <SelectTrigger id="exp_dom_type" className="mt-1">
                                    <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="None">Select type</SelectItem>
                                    <SelectItem value="BUSINESS">BUSINESS</SelectItem>
                                    <SelectItem value="COMMON">COMMON</SelectItem>
                                    <SelectItem value="UNKNOWN">UNKNOWN</SelectItem>
                                </SelectContent>
                            </Select>
                        )}
                    />
                    {errors.condition_config?.expected_domain_type && <p className={errorTextClass}>{errors.condition_config.expected_domain_type.message}</p>}
                </div>
            )}
            { code === 'ATTR_FIELD_MATCH' && (
                <>
                    <div>
                        <Label htmlFor="attr_field_name">Attribution Field Name <span className="text-destructive">*</span></Label>
                        <Controller
                            name="condition_config.attribution_field_name"
                            control={control}
                            rules={{ required: "Field name is required" }}
                            render={({ field }) => <Input id="attr_field_name" type="text" {...field} value={field.value || ""}  className="mt-1" placeholder="e.g., utm_source" />}
                        />
                         {errors.condition_config?.attribution_field_name && <p className={errorTextClass}>{errors.condition_config.attribution_field_name.message}</p>}
                    </div>
                    <div>
                        <Label htmlFor="attr_val_match">Value to Match <span className="text-destructive">*</span></Label>
                        <Controller
                            name="condition_config.value_to_match"
                            control={control}
                            rules={{ required: "Value is required" }}
                            render={({ field }) => <Input id="attr_val_match" type="text" {...field} value={field.value || ""} className="mt-1" />}
                        />
                        {errors.condition_config?.value_to_match && <p className={errorTextClass}>{errors.condition_config.value_to_match.message}</p>}
                    </div>
                </>
            )}
            { code === 'LEAD_AGE_HOURS_CONFIG_GT' && (
                <div>
                    <Label htmlFor="hours_gt">Hours (Lead must be older than this) <span className="text-destructive">*</span></Label>
                    <Controller
                        name="condition_config.hours"
                        control={control}
                        rules={{ required: "Hours value is required", min: { value: 1, message: "Must be at least 1 hour" }, valueAsNumber: true }}
                        render={({ field }) => <Input id="hours_gt" type="number" {...field} value={field.value || ""} onChange={e => field.onChange(e.target.value === '' ? '' : parseInt(e.target.value))} className="mt-1" />}
                    />
                    {errors.condition_config?.hours && <p className={errorTextClass}>{errors.condition_config.hours.message}</p>}
                </div>
            )}
            { !['Q_MULTI_OPTION_ALL_MATCH', 'Q_SINGLE_OPTION_MATCH', 'LOC_STATE_MATCH', 'LOC_DISTRICT_MATCH', 'LOC_POSTCODE_MATCH', 'EMAIL_DOMAIN_TYPE_MATCH', 'ATTR_FIELD_MATCH', 'LEAD_AGE_HOURS_CONFIG_GT'].includes(code) && (
                 <p className="text-sm text-destructive mt-2">Unsupported or unknown condition type. Config schema guidance: <pre className="text-xs bg-muted p-2 rounded overflow-x-auto mt-1">{JSON.stringify(definition.config_schema_guidance, null, 2)}</pre></p>
            )}
        </div>
    )
};
const DynamicConditionConfigForm = React.memo(DynamicConditionConfigFormComponent);


export default function PricingRuleForm({
    initialData, onSave, onCancel, isLoading, questionList
}) {
    const [conditionDefinitions, setConditionDefinitions] = useState([]);
    const [selectedConditionDefinition, setSelectedConditionDefinition] = useState(null);

    const { control, handleSubmit, reset, watch, setValue, formState: { errors, isDirty } } = useForm({
        defaultValues: defaultRuleValues
    });

    const watchedModifierType = watch("modifier_type");
    const watchedConditionDefinitionId = watch("condition_definition_id");
    const watchedConditionConfig = watch("condition_config");
    const debouncedConditionConfig = useDebouncedValue(watchedConditionConfig, 300);

    useEffect(() => {
        const fetchDefs = async () => {
            try {
                const response = await getPricingConditionDefinitions({ active_only: true, limit: 200 });
                setConditionDefinitions(response.data || []);
            } catch (error) {
                console.error("Failed to fetch condition definitions:", error);
            }
        };
        fetchDefs();
    }, []);

    useEffect(() => {
        if (watchedConditionDefinitionId) {
            const currentDef = conditionDefinitions.find(def => def.id === parseInt(watchedConditionDefinitionId));
            setSelectedConditionDefinition(currentDef || null);
            setValue("condition_config", {}, { shouldValidate: false, shouldDirty: true });
        } else {
            setSelectedConditionDefinition(null);
            setValue("condition_config", {}, { shouldValidate: false, shouldDirty: true });
        }
    }, [watchedConditionDefinitionId, conditionDefinitions, setValue]);

    useEffect(() => {
        if (selectedConditionDefinition) {
            let autoName = selectedConditionDefinition.name;
            const config = debouncedConditionConfig || {};
            const code = selectedConditionDefinition.condition_code;

            if ((code === 'Q_MULTI_OPTION_ALL_MATCH' || code === 'Q_SINGLE_OPTION_MATCH') && config.category_question_id) {
                const q = questionList.find(q => q.id === parseInt(config.category_question_id));
                if (q) autoName += `: ${q.label || q.value}`;
                if (code === 'Q_SINGLE_OPTION_MATCH' && config.required_option_id && q?.options) {
                    const opt = q.options.find(o => o.id === parseInt(config.required_option_id));
                    if (opt) autoName += ` -> ${opt.label || opt.value}`;
                } else if (code === 'Q_MULTI_OPTION_ALL_MATCH' && config.required_option_ids?.length && q?.options) {
                     const selectedOptions = q.options
                        .filter(opt => config.required_option_ids.includes(opt.id))
                        .map(opt => opt.label || opt.value)
                        .join(' & ');
                    if (selectedOptions) autoName += ` -> [${selectedOptions}]`;
                }
            } else if (code && code.startsWith('LOC_') && config.value_to_match) {
                autoName += `: ${config.value_to_match}`;
            } else if (code === 'EMAIL_DOMAIN_TYPE_MATCH' && config.expected_domain_type) {
                autoName += `: ${config.expected_domain_type}`;
            } else if (code === 'ATTR_FIELD_MATCH' && config.attribution_field_name) {
                autoName += `: ${config.attribution_field_name} = ${config.value_to_match || 'any'}`;
            }
             if (watch("rule_name") !== autoName) {
               setValue("rule_name", autoName, { shouldDirty: true, shouldValidate: true });
            }
        } else {
             if (watch("rule_name") !== "Rule (select condition type)") {
                setValue("rule_name", "Rule (select condition type)", { shouldDirty: !!initialData });
             }
        }
    }, [selectedConditionDefinition, debouncedConditionConfig, questionList, setValue, initialData, watch]);

    const prevInitialDataRef = useRef();
    useEffect(() => {
        // This logic to derive action/values from initialData needs careful review
        // to ensure it's compatible with how shadcn/ui Select/Input manage their state,
        // especially around `value={field.value || ""}` patterns.
        if (prevInitialDataRef.current !== initialData) {
            prevInitialDataRef.current = initialData;
        }

        if (initialData) {
            let derivedAction = initialData.modifier_action || defaultRuleValues.modifier_action;
            let derivedAbsoluteValue = defaultRuleValues.modifier_value_absolute;
            let derivedPercentageDisplay = defaultRuleValues.modifier_value_percentage_display;

            if (initialData.modifier_type === ModifierTypeEnum.ABSOLUTE) {
                const currentStoredAbsoluteValue = initialData.modifier_value_absolute;
                if (!initialData.modifier_action && currentStoredAbsoluteValue !== null) {
                    derivedAction = currentStoredAbsoluteValue < 0 ? ModifierActionEnum.DECREASE : ModifierActionEnum.INCREASE;
                    derivedAbsoluteValue = Math.abs(currentStoredAbsoluteValue);
                } else if (initialData.modifier_action) {
                    derivedAction = initialData.modifier_action;
                    derivedAbsoluteValue = initialData.modifier_value_absolute !== null ? initialData.modifier_value_absolute : 0;
                }
            } else if (initialData.modifier_type === ModifierTypeEnum.PERCENTAGE) {
                const currentPercentageRate = initialData.modifier_value_percentage_rate;
                if (!initialData.modifier_action && currentPercentageRate !== null && currentPercentageRate !== undefined) {
                    derivedAction = currentPercentageRate < 0 ? ModifierActionEnum.DECREASE : ModifierActionEnum.INCREASE;
                    derivedPercentageDisplay = Math.abs(parseFloat(currentPercentageRate) * 100);
                } else if (initialData.modifier_action) {
                    derivedAction = initialData.modifier_action;
                    derivedPercentageDisplay = initialData.modifier_value_percentage_rate !== null && initialData.modifier_value_percentage_rate !== undefined 
                        ? Math.abs(parseFloat(initialData.modifier_value_percentage_rate) * 100)
                        : 0;
                } else if (initialData.modifier_value_percentage_display !== undefined) {
                    derivedPercentageDisplay = initialData.modifier_value_percentage_display;
                }
            }

            const populatedData = {
                ...defaultRuleValues,
                ...initialData,
                modifier_action: derivedAction,
                modifier_value_absolute: initialData.modifier_type === ModifierTypeEnum.ABSOLUTE ? derivedAbsoluteValue : defaultRuleValues.modifier_value_absolute,
                modifier_value_percentage_display: initialData.modifier_type === ModifierTypeEnum.PERCENTAGE ? derivedPercentageDisplay : defaultRuleValues.modifier_value_percentage_display,
                condition_config: initialData.condition_config || {},
            };
            reset(populatedData);
        } else {
            reset(defaultRuleValues);
        }
    }, [initialData, reset]);

    const onSubmitHandler = (data) => {

        console.log("On Submit data",data)
        const payload = {
            rule_name: data.rule_name,
            description: data.description,
            is_active: data.is_active,
            modifier_type: data.modifier_type,
            condition_definition_id: parseInt(data.condition_definition_id),
            condition_config: data.condition_config,
            modifier_value_absolute: null,
            modifier_value_percentage_rate: null,
            modifier_action: data.modifier_action,
        };

        if (data.modifier_type === ModifierTypeEnum.ABSOLUTE) {
            const absValue = data.modifier_value_absolute !== null && data.modifier_value_absolute !== ''
                ? Math.abs(parseInt(data.modifier_value_absolute, 10)) : null;
            payload.modifier_value_absolute = absValue;
        } else if (data.modifier_type === ModifierTypeEnum.PERCENTAGE) {
            const displayVal = parseFloat(data.modifier_value_percentage_display);
            if (!isNaN(displayVal)) {
                const percentageRate = Math.abs(displayVal) / 100;
                console.log("Initial create % value", percentageRate)
                payload.modifier_value_percentage_rate = parseFloat(percentageRate.toFixed(4));
                console.log("Final create % value", payload.modifier_value_percentage_rate)
            }
        }
        
        if (data.modifier_action === ModifierActionEnum.DECREASE) {
            if(payload.modifier_value_percentage_rate){
                payload.modifier_value_percentage_rate = -payload.modifier_value_percentage_rate;
            }
            if(payload.modifier_value_absolute){
                payload.modifier_value_absolute = -payload.modifier_value_absolute;
            }
        }

        onSave(payload);
    };

    return (
        <Dialog open={true} onOpenChange={onCancel}> {/* Manages its own open state via prop / callback */}
            <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto"> {/* [cite: 25] */}
                <DialogHeader>
                    <DialogTitle>{initialData ? 'Edit Pricing Rule' : 'Create New Pricing Rule'}</DialogTitle>
                    <DialogClose asChild>
                        <Button type="button" variant="ghost" size="icon" className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
                            <XIcon className="h-4 w-4" /> {/* [cite: 9] */}
                            <span className="sr-only">Close</span>
                        </Button>
                    </DialogClose>
                </DialogHeader>
                
                {/*<form onSubmit={handleSubmit(onSubmitHandler)} className="space-y-5 pt-4">*/}
                <form
   onSubmit={handleSubmit(
     onSubmitHandler,
    (errs) => { console.log("RHF invalid:", errs); }
   )}
   className="space-y-5 pt-4">  
                    <div>
                        <Label htmlFor="rule_name">Rule Name (Auto-generated)</Label>
                        <Controller
                            name="rule_name"
                            control={control}
                            rules={{ required: "Rule name is required." }}
                            render={({ field }) => <Input type="text" id="rule_name" {...field} className="mt-1 bg-muted" readOnly />} /* [cite: 1] */
                        />
                        {errors.rule_name && <p className={errorTextClass}>{errors.rule_name.message}</p>}
                    </div>

                    <div>
                        <Label htmlFor="description">Description (Optional)</Label>
                        <Controller
                            name="description"
                            control={control}
                            render={({ field }) => <Textarea id="description" {...field} rows="2" className="mt-1" value={field.value || ""} />}
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                        <div className="md:col-span-1">
                            <Label htmlFor="modifier_type">Modifier Type <span className="text-destructive">*</span></Label>
                            <Controller
                                name="modifier_type"
                                control={control}
                                rules={{ required: "Modifier type is required" }}
                                render={({ field }) => (
                                    <Select
                                        value={field.value}
                                        onValueChange={(value) => {
                                            field.onChange(value);
                                            setValue('modifier_value_absolute', defaultRuleValues.modifier_value_absolute, { shouldValidate: true, shouldDirty: true });
                                            setValue('modifier_value_percentage_display', defaultRuleValues.modifier_value_percentage_display, { shouldValidate: true, shouldDirty: true });
                                            // Action might not need reset if it's common
                                            // setValue('modifier_action', defaultRuleValues.modifier_action, { shouldValidate: true, shouldDirty: true });
                                        }}
                                    >
                                        <SelectTrigger id="modifier_type" className="mt-1">
                                            <SelectValue placeholder="Select Modifier Type"/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value={ModifierTypeEnum.ABSOLUTE}>Absolute (Coins)</SelectItem>
                                            <SelectItem value={ModifierTypeEnum.PERCENTAGE}>Percentage</SelectItem>
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                            {errors.modifier_type && <p className={errorTextClass}>{errors.modifier_type.message}</p>}
                        </div>

                        {watchedModifierType && ( /* Common Action Field */
                             <div className="md:col-span-1">
                                <Label htmlFor="modifier_action">Action <span className="text-destructive">*</span></Label>
                                <Controller
                                    name="modifier_action"
                                    control={control}
                                    rules={{ required: "Action is required" }}
                                    render={({ field }) => (
                                        <Select value={field.value} onValueChange={field.onChange}>
                                            <SelectTrigger id="modifier_action" className="mt-1">
                                                <SelectValue placeholder="Select Action" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value={ModifierActionEnum.INCREASE}>Increase</SelectItem>
                                                <SelectItem value={ModifierActionEnum.DECREASE}>Decrease</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                                {errors.modifier_action && <p className={errorTextClass}>{errors.modifier_action.message}</p>}
                            </div>
                        )}

                        {watchedModifierType === ModifierTypeEnum.ABSOLUTE && (
                            <div className="md:col-span-1">
                                <Label htmlFor="modifier_value_absolute">Coins <span className="text-destructive">*</span></Label>
                                <Controller
                                    name="modifier_value_absolute"
                                    control={control}
                                    rules={{
                                        required: "Value is required",
                                        min: { value: 0, message: "Value must be zero or positive." },
                                        validate: value => !isNaN(value) || "Must be a valid number.",
                                        valueAsNumber: true,
                                    }}
                                    render={({ field }) => (
                                        <Input
                                            type="number"
                                            id="modifier_value_absolute"
                                            {...field}
                                            value={field.value ?? ""}
                                            onChange={e => field.onChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
                                            className="mt-1"
                                            placeholder="e.g., 50"
                                        />
                                    )}
                                />
                                {errors.modifier_value_absolute && <p className={errorTextClass}>{errors.modifier_value_absolute.message}</p>}
                            </div>
                        )}
                        {watchedModifierType === ModifierTypeEnum.PERCENTAGE && (
                             <div className="md:col-span-1">
                                <Label htmlFor="modifier_value_percentage_display">Value (%) <span className="text-destructive">*</span></Label>
                                <Controller
                                    name="modifier_value_percentage_display"
                                    control={control}
                                    rules={{
                                        required: "Value is required",
                                        min: { value: 0, message: "Percentage must be zero or positive." },
                                        max: { value: 1000, message: "Percentage seems too high (max 1000%)." },
                                        validate: value => !isNaN(value) || "Must be a valid number.",
                                        valueAsNumber: true,
                                    }}
                                    render={({ field }) => (
                                        <Input
                                            type="number"
                                            step="1"
                                            id="modifier_value_percentage_display"
                                            {...field}
                                            value={field.value ?? ""}
                                            onChange={e => field.onChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
                                            className="mt-1"
                                            placeholder="e.g., 10 for 10%"
                                        />
                                    )}
                                />
                                {errors.modifier_value_percentage_display && <p className={errorTextClass}>{errors.modifier_value_percentage_display.message}</p>}
                            </div>
                        )}
                    </div>

                    <div>
                        <Label htmlFor="condition_definition_id">Condition Type <span className="text-destructive">*</span></Label>
                        <Controller
                            name="condition_definition_id"
                            control={control}
                            rules={{ required: "Condition type is required" }}
                            render={({ field }) => (
                                <Select
                                    value={field.value ? String(field.value) : "None"}
                                    onValueChange={(value) => field.onChange(value ? parseInt(value, 10) : '')}
                                >
                                    <SelectTrigger id="condition_definition_id" className="mt-1">
                                        <SelectValue placeholder="Select Condition Type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {conditionDefinitions.map(def => (
                                            <SelectItem key={def.id} value={String(def.id)}>{def.name} ({def.condition_code})</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        />
                        {errors.condition_definition_id && <p className={errorTextClass}>{errors.condition_definition_id.message}</p>}
                    </div>

                    {selectedConditionDefinition && (
                        <DynamicConditionConfigForm
                            definition={selectedConditionDefinition}
                            control={control}
                            errors={errors}
                            questionList={questionList}
                            setValue={setValue}
                        />
                    )}

                    <div className="flex items-center space-x-2 pt-2">
                        <Controller
                            name="is_active"
                            control={control}
                            render={({ field }) => <Checkbox id="is_active_rule" checked={field.value} onCheckedChange={field.onChange} />}
                        />
                        <Label htmlFor="is_active_rule" className="font-normal">Rule is Active</Label>
                    </div>

                    <DialogFooter className="pt-6"> {/* DialogFooter for modal buttons */}
                        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
                            Cancel
                        </Button> {/* [cite: 12] */}
                        <Button type="submit" variant="default" isLoading={isLoading} disabled={isLoading || (!isDirty && !!initialData)}>
                            {isLoading ? "Saving..." : (initialData ? 'Save Changes' : 'Create Rule')}
                        </Button> {/* [cite: 10] */}
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}